"use client"

import { zodResolver } from "@hookform/resolvers/zod";
import { Plus, X } from "lucide-react";
import { useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";
import { setActiveTab, setExtractedData } from "../../store/features/cvUpload/cvUploadSlice";
import { useAppDispatch, useAppSelector } from "../../store/hooks";

import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import ReactSelect from "@/components/ui/react-select";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CV_UPLOAD_OPTIONS } from "./types";

// Define zod schema for the form
const formSchema = z.object({
  name: z.string().min(1, "Name is required"),
  birthYear: z.number().optional(),
  email: z.string().email("Invalid email address"),
  phone: z.string().min(1, "Phone number is required"),
  position: z.string().min(1, "Position is required"),
  level: z.string().min(1, "Level is required"),
  yearsOfExperience: z.number().min(1, "Years of experience is required"),
  managementExperience: z.number().optional(),
  languages: z.array(
    z.object({
      language: z.string().min(1, "Language is required"),
      level: z.number().min(1, "Proficiency level is required")
    })
  ),
  preferredLocation: z.string().min(1, "Preferred location is required"),
  skills: z.array(z.string()).min(1, "At least one skill is required")
});

type FormValues = z.infer<typeof formSchema>;



export function EditTab() {
  const dispatch = useAppDispatch();
  const { extractedData } = useAppSelector((state) => state.cvUpload);

  // Initialize with empty data if no extractedData exists
  const profileData = extractedData || {
    name: "", birthYear: undefined, email: "", phone: "", position: "", level: "",
    yearsOfExperience: undefined, managementExperience: undefined, languages: [],
    preferredLocation: "", skills: [], missingFields: []
  };

  // Initialize react-hook-form
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: profileData.name || "",
      birthYear: profileData.birthYear,
      email: profileData.email || "",
      phone: profileData.phone || "",
      position: profileData.position || "",
      level: profileData.level || "",
      yearsOfExperience: profileData.yearsOfExperience,
      managementExperience: profileData.managementExperience,
      languages: profileData.languages?.length ? profileData.languages : [{ language: '', level: undefined }],
      preferredLocation: profileData.preferredLocation || "",
      skills: profileData.skills || []
    }
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "languages"
  });

  const handleSubmit = (data: FormValues) => {
    dispatch(setExtractedData({ ...profileData, ...data, missingFields: [] }));
    dispatch(setActiveTab("review"));
  };

  return (
    <div className="space-y-4">

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 gap-3">
            {/* Row 1: Fullname, Birthyear, Preferred Location */}
            <div className="grid grid-cols-3 gap-3">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs">Full Name</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        className="h-9 text-sm"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="birthYear"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs">Birth Year (if applicable)</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="number"
                        className="h-9 text-sm"
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.valueAsNumber)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="preferredLocation"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs">Preferred Work Location</FormLabel>
                    <FormControl>
                      <ReactSelect
                        instanceId="manual-preferredLocation"
                        options={CV_UPLOAD_OPTIONS.locations}
                        isMulti={true}
                        value={CV_UPLOAD_OPTIONS.locations.filter(option =>
                          field.value.includes(option.label)
                        )}
                        onChange={(values) =>
                          field.onChange(values.map(value => value.label).join(', '))
                        }
                        placeholder="HN, HCM, Remote..."
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Row 2: Email, Phone */}
            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs">Email</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        {...field}
                        className="h-9 text-sm"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs">Phone Number</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        className="h-9 text-sm"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Row 3: Position, Level, YOE, Management YOE */}
            <div className="grid grid-cols-4 gap-2">
              <FormField
                control={form.control}
                name="position"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs">Position</FormLabel>
                    <FormControl className="h-9 text-xs">
                      <ReactSelect
                        inputId="manual-position"
                        options={CV_UPLOAD_OPTIONS.positions}
                        value={CV_UPLOAD_OPTIONS.positions.find(option => option.value === field.value)}
                        onChange={(option) => field.onChange(option?.value || '')}
                        placeholder="Select position"
                        className="text-xs"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="level"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs">Professional Level</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger id="manual-level" className="h-9 text-sm">
                          <SelectValue placeholder="Select level" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="Junior">Junior</SelectItem>
                        <SelectItem value="Middle">Middle</SelectItem>
                        <SelectItem value="Senior">Senior</SelectItem>
                        <SelectItem value="Lead">Lead</SelectItem>
                        <SelectItem value="Principal">Principal</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="yearsOfExperience"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs">Years of Experience</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="number"
                        className="h-9 text-sm"
                        placeholder="Years of working"
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.valueAsNumber)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="managementExperience"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs">Management Experience</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="number"
                        placeholder="Years as manager"
                        className="h-9 text-sm"
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.valueAsNumber)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Row 4: Languages & Proficiency Levels */}
            <div>
              <Label className="text-xs">Languages & Proficiency Levels</Label>
              <div className="space-y-1.5">
                {fields.map((field, index) => (
                  <div key={field.id} className="flex items-center gap-2">
                    <FormField
                      control={form.control}
                      name={`languages.${index}.language`}
                      render={({ field }) => (
                        <FormItem className="w-64">
                          <FormControl>
                            <ReactSelect
                              instanceId={`language-${index}`}
                              options={CV_UPLOAD_OPTIONS.languages}
                              onChange={(value) => field.onChange(value?.value || '')}
                              value={CV_UPLOAD_OPTIONS.languages.find(option => option.value === field.value)}
                              placeholder="Language"
                              className="text-xs"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={`languages.${index}.level`}
                      render={({ field }) => (
                        <FormItem className="h-9">
                          <FormControl>
                            <Select
                              onValueChange={(value) => field.onChange(Number(value))}
                              value={field.value?.toString()}
                            >
                              <SelectTrigger className="h-9 min-w-[140px]">
                                <SelectValue placeholder="Proficiency" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="5">Native</SelectItem>
                                <SelectItem value="4">Fluent</SelectItem>
                                <SelectItem value="3">Intermediate</SelectItem>
                                <SelectItem value="2">Basic</SelectItem>
                                <SelectItem value="1">Beginner</SelectItem>
                              </SelectContent>
                            </Select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => remove(index)}
                    >
                      <span className="sr-only">Remove</span>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}

                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-xs h-7 mt-1"
                  onClick={() => append({ language: '', level: 1 })}
                >
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  Add Language
                </Button>
              </div>
            </div>

            {/* Row 5: Skills, Techstack with autocomplete */}
            <FormField
              control={form.control}
              name="skills"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs">Skills & Tech Stack</FormLabel>
                  <FormControl>
                    <ReactSelect
                      instanceId="skills-select"
                      options={CV_UPLOAD_OPTIONS.skills}
                      isMulti={true}
                      value={CV_UPLOAD_OPTIONS.skills.filter(option =>
                        field.value.includes(option.value)
                      )}
                      onChange={(values) => field.onChange(values.map(value => value.value))}
                      placeholder="React, JavaScript, TypeScript, etc."
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="flex justify-end">
            <Button type="submit" size="sm" className="text-xs">
              Submit
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}